import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import {
  ArrowLeft,
  ImageIcon,
  LoaderCircle,
  MessageSquare,
  Mic,
  Music,
  Type,
  Upload,
  Video,
  Wallet,
  Zap,
} from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { PricingTable } from '@/features/subscriptions/components/pricing-table'
import {
  type CreditPack,
  useCheckoutMutation,
  useCreditPacksQuery,
  useWalletQuery,
} from '@/features/wallet/api'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/top-up')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' })
    }
  },
  component: TopUpPage,
})

/* ─── Types ─────────────────────────────────── */

interface CostItem {
  icon: typeof Music
  label: string
  cost: string
  note: string | null
}

/* ─── Data ──────────────────────────────────── */

function formatDollars(cents: number): string {
  return (cents / 100).toFixed(2)
}

const COST_SECTIONS: { title: string; items: CostItem[] }[] = [
  {
    title: 'Audio',
    items: [
      {
        icon: Upload,
        label: 'Upload your own track',
        cost: 'Free',
        note: null,
      },
      {
        icon: Mic,
        label: 'Use a provided voice',
        cost: '10 GP',
        note: 'Includes song generation',
      },
      {
        icon: Music,
        label: 'Generate a song',
        cost: '10 GP',
        note: 'Regeneration costs the same',
      },
      {
        icon: Mic,
        label: 'Generate with your voice sample',
        cost: '20 GP',
        note: 'Per generated song, Creator or Pro required',
      },
      {
        icon: Mic,
        label: 'Generate narration',
        cost: '5 GP',
        note: 'For a supplied or generated script',
      },
      {
        icon: Type,
        label: 'Generate lyrics',
        cost: '1 GP',
        note: 'AI writing assist',
      },
      {
        icon: Type,
        label: 'Generate a narration script',
        cost: '1 GP',
        note: 'AI writing assist',
      },
    ],
  },
  {
    title: 'Visuals',
    items: [
      {
        icon: ImageIcon,
        label: 'Generate an image',
        cost: '1 GP',
        note: 'Per image',
      },
      {
        icon: ImageIcon,
        label: 'Regenerate an image',
        cost: '5 GP',
        note: 'Per regeneration',
      },
      {
        icon: Type,
        label: 'Add lyrics to video',
        cost: '2 GP',
        note: null,
      },
    ],
  },
  {
    title: 'AI Editing',
    items: [
      {
        icon: MessageSquare,
        label: 'Chat with AI agent',
        cost: '1 GP',
        note: 'Refine visuals, layout, and timing',
      },
    ],
  },
  {
    title: 'Rendering',
    items: [
      {
        icon: Video,
        label: 'Render final video (720p)',
        cost: '15 GP',
        note: null,
      },
      {
        icon: Video,
        label: 'Render final video (1080p)',
        cost: '30 GP',
        note: null,
      },
      {
        icon: Zap,
        label: 'Render final video (4K)',
        cost: '60 GP',
        note: null,
      },
    ],
  },
]

/* ─── Animation variants ────────────────────── */

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
}

const fadeUp = {
  hidden: { opacity: 0, y: 20, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] as const },
  },
}

/* ─── Components ────────────────────────────── */

interface PackageCardProps {
  pkg: CreditPack
  isSelected: boolean
  onSelect: () => void
  onPurchase: (packId: string) => void
  isPurchasing: boolean
}

function PackageCard({
  pkg,
  isSelected,
  onSelect,
  onPurchase,
  isPurchasing,
}: PackageCardProps) {
  const priceDollars = pkg.price_cents / 100
  const perGp = (priceDollars / pkg.gp).toFixed(3)

  return (
    <button
      type="button"
      disabled={!pkg.available}
      onClick={onSelect}
      className={cn(
        'relative flex cursor-pointer flex-col gap-3 rounded-xl border bg-card p-5 text-left shadow-card transition-all duration-200 hover:shadow-card-hover disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:shadow-card',
        isSelected && 'border-primary ring-2 ring-primary/20',
      )}
    >
      {pkg.badge && (
        <Badge
          variant="secondary"
          className="absolute -top-2.5 right-4 text-[10px]"
        >
          {pkg.badge}
        </Badge>
      )}
      {!pkg.available && (
        <Badge
          variant="outline"
          className="absolute -top-2.5 right-4 bg-background text-[10px]"
        >
          Temporarily unavailable
        </Badge>
      )}

      <div className="flex items-baseline justify-between">
        <span className="text-base font-semibold">{pkg.name}</span>
        <span className="text-2xl font-bold">
          ${formatDollars(pkg.price_cents)}
        </span>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm font-medium tabular-nums">
          {pkg.gp.toLocaleString()} GP
        </span>
        <span className="text-xs tabular-nums text-muted-foreground">
          ${perGp}/GP
        </span>
      </div>

      <p className="text-xs text-muted-foreground">
        ~{pkg.estimated_videos} complete videos from start to finish
      </p>

      <AnimatePresence>
        {isSelected && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <Button
              className="mt-1 w-full"
              disabled={isPurchasing || !pkg.available}
              onClick={(e) => {
                e.stopPropagation()
                onPurchase(pkg.id)
              }}
            >
              {isPurchasing && <LoaderCircle className="size-4 animate-spin" />}
              {pkg.available ? 'Purchase' : 'Unavailable'}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </button>
  )
}

interface CostRowProps {
  item: CostItem
}

function CostRow({ item }: CostRowProps) {
  const Icon = item.icon
  const isFree = item.cost === 'Free'

  return (
    <div className="flex items-center gap-3 py-2">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted">
        <Icon className="size-4 text-muted-foreground" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-sm">{item.label}</span>
        {item.note && (
          <span className="text-xs text-muted-foreground">{item.note}</span>
        )}
      </div>
      <span
        className={cn(
          'shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold tabular-nums',
          isFree
            ? 'bg-emerald-100 text-emerald-800'
            : 'bg-foreground/5 text-muted-foreground',
        )}
      >
        {item.cost}
      </span>
    </div>
  )
}

/* ─── Page ──────────────────────────────────── */

function TopUpPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const walletQuery = useWalletQuery()
  const packsQuery = useCreditPacksQuery()
  const checkoutMutation = useCheckoutMutation()

  return (
    <div className="relative min-h-screen px-4 pb-16 pt-6 sm:px-6 lg:px-8">
      {/* Header */}
      <motion.header
        className="mx-auto mb-8 flex max-w-3xl items-center gap-3"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        <Button variant="ghost" size="icon" className="shrink-0" asChild>
          <Link to="/dashboard" aria-label="Back to dashboard">
            <ArrowLeft className="size-5" />
          </Link>
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">Top Up</h1>
      </motion.header>

      <motion.div
        className="mx-auto flex max-w-3xl flex-col gap-10"
        variants={stagger}
        initial="hidden"
        animate="visible"
      >
        {/* Packages + balance */}
        <motion.section variants={fadeUp}>
          <div className="mb-4 flex items-end justify-between">
            <h2 className="text-lg font-semibold tracking-tight">
              Choose a package
            </h2>
            <div className="flex items-center gap-2">
              <Wallet className="size-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Balance:</span>
              <span className="text-sm font-bold tabular-nums">
                {walletQuery.isLoading
                  ? '...'
                  : (walletQuery.data?.balance ?? 0).toLocaleString()}{' '}
                GP
              </span>
            </div>
          </div>
          {packsQuery.isLoading ? (
            <div className="flex items-center justify-center gap-2 rounded-xl border border-dashed p-12 text-sm text-muted-foreground">
              <LoaderCircle className="size-4 animate-spin" />
              Loading packages...
            </div>
          ) : packsQuery.isError ? (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-300">
              Could not load one-time GP packs. Subscription options remain
              available below.
            </div>
          ) : checkoutMutation.isError ? (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-300">
              Could not start checkout. Try again in a moment.
            </div>
          ) : (
            <div className="space-y-3">
              {(packsQuery.data ?? []).length === 0 && (
                <div className="rounded-xl border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
                  No one-time GP packs are available right now.
                </div>
              )}
              {(packsQuery.data ?? []).every((pkg) => !pkg.available) && (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
                  One-time GP packs are temporarily unavailable. Subscription
                  plans below remain available and include recurring monthly GP.
                </div>
              )}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {(packsQuery.data ?? []).map((pkg) => (
                  <PackageCard
                    key={pkg.id}
                    pkg={pkg}
                    isSelected={selectedId === pkg.id}
                    onSelect={() =>
                      setSelectedId((prev) => (prev === pkg.id ? null : pkg.id))
                    }
                    onPurchase={(id) => checkoutMutation.mutate(id)}
                    isPurchasing={
                      checkoutMutation.isPending &&
                      checkoutMutation.variables === pkg.id
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </motion.section>

        {/* Subscription plans — added below the packs so the page now
            covers both purchase modes in one place. */}
        <motion.section variants={fadeUp}>
          <div className="mb-4">
            <h2 className="text-lg font-semibold tracking-tight">
              Or pick a plan
            </h2>
            <p className="text-sm text-muted-foreground">
              Recurring GP each month plus tier perks like 4K, voice-sample
              generation, and longer videos.
            </p>
          </div>
          <PricingTable dense />
        </motion.section>

        {/* Cost breakdown */}
        <motion.section variants={fadeUp}>
          <div className="mb-1">
            <h2 className="text-lg font-semibold tracking-tight">
              Where your GP goes
            </h2>
            <p className="text-sm text-muted-foreground">
              Every action that costs Generation Points, so you always know what
              you are paying for.
            </p>
          </div>

          <div className="mt-5 flex flex-col gap-6">
            {COST_SECTIONS.map((section) => (
              <div key={section.title}>
                <h3 className="mb-2 text-sm font-medium text-muted-foreground">
                  {section.title}
                </h3>
                <div className="divide-y rounded-xl border bg-card px-4 shadow-card">
                  {section.items.map((item) => (
                    <CostRow key={item.label} item={item} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Estimates vary with video length, visual count, regenerations, and
            render quality.
          </p>
        </motion.section>
      </motion.div>
    </div>
  )
}
